import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { BusinessUnitToggle, MetricType, ROIBreakdownEnum, ViewToggle } from "@/shared/types";
import { hashStorage } from "@/shared/utils/hashStorage";
import { Funnel, FunnelType } from "@/Utils/hierarchy";

import { DEFAULT_COLUMNS } from "../constants/defaults";

const STORE_NAME = "Geographies";

interface GeographiesState {
  filters: {
    targetNode: string | null;
    geographies: string[];
    channels: string[];
  };
  chart: {
    chartView: ViewToggle;
    chartBreakdown: ROIBreakdownEnum;
    isAscending: boolean;
    filterInsufficientData: boolean;
  };
  grid: {
    gridView: ViewToggle;
    businessUnitToggle: BusinessUnitToggle;
    metricType: MetricType;
    activeColumns: string[];
    spend: FunnelType[];
    revenue: FunnelType[];
    roi: FunnelType[];
  };
}

interface GeographiesActions {
  setTargetNode: (node: string | null) => void;
  setGeographies: (campaigns: string[]) => void;
  setChannels: (channels: string[]) => void;
  setChartView: (view: ViewToggle) => void;
  setChartBreakdown: (breakdown: ROIBreakdownEnum) => void;
  setIsAscending: (isAscending: boolean) => void;
  setFilterInsufficientData: (filterInsufficientData: boolean) => void;
  setGridView: (view: ViewToggle) => void;
  setBusinessUnitToggle: (businessUnitToggle: BusinessUnitToggle) => void;
  setMetricType: (metricType: MetricType) => void;
  setSpend: (spend: FunnelType[]) => void;
  setRevenue: (revenue: FunnelType[]) => void;
  setROI: (roi: FunnelType[]) => void;
  setActiveColumns: (columns: string[]) => void;
}

type GeographiesStore = GeographiesState & GeographiesActions;

const defaultState: GeographiesState = {
  filters: {
    targetNode: null,
    geographies: [],
    channels: [],
  },
  chart: {
    chartView: ViewToggle.INSIGHT,
    chartBreakdown: ROIBreakdownEnum.SPEND_DOLLARS,
    isAscending: true,
    filterInsufficientData: true,
  },
  grid: {
    gridView: ViewToggle.INSIGHT,
    businessUnitToggle: BusinessUnitToggle.AGGREGATE,
    metricType: MetricType.MARKETING_ROI,
    activeColumns: DEFAULT_COLUMNS,
    spend: [Funnel.Total],
    revenue: [Funnel.Total],
    roi: [Funnel.Total],
  },
};

export const useGeographiesStore = create<GeographiesStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNode: (node) =>
          set((state) => {
            state.filters.targetNode = node;
          }),
        setGeographies: (geographies) =>
          set((state) => {
            state.filters.geographies = geographies;
          }),
        setChannels: (channels) =>
          set((state) => {
            state.filters.channels = channels;
          }),
        setChartView: (view) =>
          set((state) => {
            state.chart.chartView = view;
          }),
        setChartBreakdown: (breakdown) =>
          set((state) => {
            state.chart.chartBreakdown = breakdown;
          }),
        setIsAscending: (isAscending) =>
          set((state) => {
            state.chart.isAscending = isAscending;
          }),
        setFilterInsufficientData: (filterInsufficientData) =>
          set((state) => {
            state.chart.filterInsufficientData = filterInsufficientData;
          }),
        setGridView: (view) =>
          set((state) => {
            state.grid.gridView = view;
          }),
        setBusinessUnitToggle: (businessUnitToggle) =>
          set((state) => {
            state.grid.businessUnitToggle = businessUnitToggle;
          }),
        setMetricType: (metricType) =>
          set((state) => {
            state.grid.metricType = metricType;
          }),
        setSpend: (spend) =>
          set((state) => {
            state.grid.spend = spend;
          }),
        setRevenue: (revenue) =>
          set((state) => {
            state.grid.revenue = revenue;
          }),
        setROI: (roi) =>
          set((state) => {
            state.grid.roi = roi;
          }),
        setActiveColumns: (columns) =>
          set((state) => {
            state.grid.activeColumns = columns;
          }),
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
